import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import loadable from '@loadable/component';
import { storage, log } from '@guardian/libs';
import { getWeeklyArticleHistory, incrementWeeklyArticleCount, } from '@guardian/support-dotcom-components';
import { ReaderRevenueLinks } from './ReaderRevenueLinks';
import { SlotBodyEnd } from './SlotBodyEnd/SlotBodyEnd';
import { ContributionSlot } from './ContributionSlot';
import { StickyBottomBanner } from './StickyBottomBanner/StickyBottomBanner';
import { SignInGateSelector } from './SignInGate/SignInGateSelector';
import { AudioAtomWrapper } from './AudioAtomWrapper';
import { Portal } from './Portal';
import { HydrateOnce, HydrateInteractiveOnce } from './HydrateOnce';
import { decideTheme } from '../lib/decideTheme';
import { decideDisplay } from '../lib/decideDisplay';
import { decideDesign } from '../lib/decideDesign';
import { useOnce } from '../lib/useOnce';
import { incrementAlreadyVisited } from '../lib/alreadyVisited';
import { incrementDailyArticleCount } from '../lib/dailyArticleCount';
import { hasOptedOutOfArticleCount } from '../lib/contributions';
import { buildAdTargeting } from '../../lib/ad-targeting';
import { buildBrazeMessages } from '../lib/braze/buildBrazeMessages';
import { GetMatchTabs } from './GetMatchTabs';
import { getOphanRecordFunction } from '../browser/ophan/ophan';
let renderCount = 0;
export const App = ({ CAPI }) => {
    log('dotcom', `App.tsx render #${(renderCount += 1)}`);
    const [brazeMessages, setBrazeMessages] = useState();
    const pageViewId = window.guardian?.config?.ophan?.pageViewId;
    const [asyncArticleCount, setAsyncArticleCount] = useState();
    const ophanRecord = getOphanRecordFunction();
    useEffect(() => {
        incrementAlreadyVisited();
    }, []);
    // Log an article view using the Slot Machine client lib
    // This function must be called once per article serving.
    // We should monitor this function call to ensure it only happens within an
    // article pages when other pages are supported by DCR.
    useEffect(() => {
        const incrementArticleCountsIfConsented = async () => {
            const hasOptedOut = await hasOptedOutOfArticleCount();
            if (!hasOptedOut) {
                incrementDailyArticleCount();
                incrementWeeklyArticleCount(storage.local, CAPI.pageId, CAPI.config.keywordIds.split(','));
            }
        };
        setAsyncArticleCount(incrementArticleCountsIfConsented().then(() => getWeeklyArticleHistory(storage.local)));
    }, [CAPI.pageId, CAPI.config.keywordIds]);
    useEffect(() => {
        // Used internally only, so only import each function on demand
        const loadAndRun = (key) => (asExistingSupporter) => import(
        /* webpackChunkName: "readerRevenueDevUtils" */ '../lib/readerRevenueDevUtils')
            .then((utils) => utils[key](asExistingSupporter, CAPI.shouldHideReaderRevenue))
            /* eslint-disable no-console */
            .catch((error) => console.log('Error loading readerRevenueDevUtils', error));
        /* eslint-enable no-console */
        if (window && window.guardian) {
            window.guardian.readerRevenue = {
                changeGeolocation: loadAndRun('changeGeolocation'),
                showMeTheEpic: loadAndRun('showMeTheEpic'),
                showMeTheBanner: loadAndRun('showMeTheBanner'),
                showNextVariant: loadAndRun('showNextVariant'),
                showPreviousVariant: loadAndRun('showPreviousVariant'),
            };
        }
    }, [CAPI.shouldHideReaderRevenue]);
    useOnce(() => {
        setBrazeMessages(buildBrazeMessages(CAPI.config.idApiUrl));
    }, [CAPI.config.idApiUrl]);
    const display = decideDisplay(CAPI.format);
    const design = decideDesign(CAPI.format);
    const pillar = decideTheme(CAPI.format);
    const format = {
        display,
        design,
        theme: pillar,
    };
    const adTargeting = buildAdTargeting({
        isAdFreeUser: CAPI.isAdFreeUser,
        isSensitive: CAPI.config.isSensitive,
        videoDuration: CAPI.config.videoDuration,
        edition: CAPI.config.edition,
        section: CAPI.config.section,
        sharedAdTargeting: CAPI.config.sharedAdTargeting,
        adUnit: CAPI.config.adUnit,
    });
    // There are docs on loadable in ./docs/loadable-components.md
    const YoutubeBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.YoutubeBlockElement').length > 0) {
            return import('./YoutubeBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.YoutubeBlockComponent,
    });
    const InteractiveBlockComponent = loadable(() => {
        if (CAPI.elementsToHydrate.filter((element) => element._type ===
            'model.dotcomrendering.pageElements.InteractiveBlockElement').length > 0) {
            return import('./InteractiveBlockComponent');
        }
        return Promise.reject();
    }, {
        resolveComponent: (module) => module.InteractiveBlockComponent,
    });
    // We use this function to filter the elementsToHydrate array by a particular
    // type so that we can hydrate them. We use T to force the type and keep TS
    // content because *we* know that if _type equals a thing then the type is
    // guaranteed but TS isn't so sure and needs assurance
    const elementsByType = (elements, type) => elements.filter((element) => element._type === type);
    const youTubeAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.YoutubeBlockElement');
    const audioAtoms = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.AudioAtomBlockElement');
    const interactiveElements = elementsByType(CAPI.elementsToHydrate, 'model.dotcomrendering.pageElements.InteractiveBlockElement');
    return (
    // Do you need to HydrateOnce or do you want a Portal?
    //
    // HydrateOnce: If your component is server rendered and you're hydrating it with
    // more data or making it interactive on the client and you do not need to access
    // global application state.
    //
    // Portal: If your component is not server rendered but a pure client-side component
    // and/or you want to access global application state, you want to use a Portal.
    //
    // Note: Both require a 'root' element that needs to be server rendered.
    _jsxs(React.StrictMode, { children: [_jsx(Portal, Object.assign({ rootId: "reader-revenue-links-header" }, { children: _jsx(ReaderRevenueLinks, { urls: CAPI.nav.readerRevenueLinks.header, edition: CAPI.editionId, dataLinkNamePrefix: "nav2 : ", inHeader: true, remoteHeaderEnabled: CAPI.config.remoteHeader, pageViewId: pageViewId, contributionsServiceUrl: CAPI.contributionsServiceUrl, ophanRecord: ophanRecord }, void 0) }), void 0), youTubeAtoms.map((youTubeAtom) => (_jsx(HydrateOnce, Object.assign({ rootId: youTubeAtom.elementId }, { children: _jsx(YoutubeBlockComponent, { format: format, hideCaption: false, 
                    // eslint-disable-next-line jsx-a11y/aria-role
                    role: "inline", adTargeting: adTargeting, isMainMedia: false, id: youTubeAtom.id, assetId: youTubeAtom.assetId, expired: youTubeAtom.expired, overrideImage: youTubeAtom.overrideImage, posterImage: youTubeAtom.posterImage, duration: youTubeAtom.duration, mediaTitle: youTubeAtom.mediaTitle, altText: youTubeAtom.altText }, void 0) }), void 0))), interactiveElements.map((interactiveBlock) => (_jsx(HydrateInteractiveOnce, Object.assign({ rootId: interactiveBlock.elementId }, { children: _jsx(InteractiveBlockComponent, { url: interactiveBlock.url, scriptUrl: interactiveBlock.scriptUrl, alt: interactiveBlock.alt, role: interactiveBlock.role, caption: interactiveBlock.caption, format: format }, void 0) }), void 0))), CAPI.matchUrl && (_jsx(Portal, Object.assign({ rootId: "match-tabs" }, { children: _jsx(GetMatchTabs, { matchUrl: CAPI.matchUrl, format: format }, void 0) }), void 0)), _jsx(Portal, Object.assign({ rootId: "top-right-ad-slot" }, { children: _jsx(ContributionSlot, { shouldHideReaderRevenue: CAPI.shouldHideReaderRevenue, isPaidContent: CAPI.pageType.isPaidContent }, void 0) }), void 0), audioAtoms.map((audioAtom) => (_jsx(HydrateOnce, Object.assign({ rootId: audioAtom.elementId }, { children: _jsx(AudioAtomWrapper, { id: audioAtom.id, trackUrl: audioAtom.trackUrl, kicker: audioAtom.kicker, title: audioAtom.title, duration: audioAtom.duration, pillar: pillar, contentIsNotSensitive: !CAPI.config.isSensitive, aCastisEnabled: CAPI.config.switches.acast, readerCanBeShownAds: !CAPI.isAdFreeUser }, void 0) }), void 0))), _jsx(Portal, Object.assign({ rootId: "slot-body-end" }, { children: _jsx(SlotBodyEnd, { contentType: CAPI.contentType, sectionName: CAPI.sectionName, sectionId: CAPI.config.section, shouldHideReaderRevenue: CAPI.shouldHideReaderRevenue, isMinuteArticle: CAPI.pageType.isMinuteArticle, isPaidContent: CAPI.pageType.isPaidContent, tags: CAPI.tags, contributionsServiceUrl: CAPI.contributionsServiceUrl, brazeMessages: brazeMessages, idApiUrl: CAPI.config.idApiUrl, stage: CAPI.stage, asyncArticleCount: asyncArticleCount }, void 0) }), void 0), _jsx(Portal, Object.assign({ rootId: "sign-in-gate" }, { children: _jsx(SignInGateSelector, { format: format, contentType: CAPI.contentType, sectionName: CAPI.sectionName, tags: CAPI.tags, isPaidContent: CAPI.pageType.isPaidContent, isPreview: !!CAPI.isPreview, host: CAPI.config.host, pageId: CAPI.pageId, idUrl: CAPI.config.idUrl, pageViewId: pageViewId }, void 0) }), void 0), _jsx(Portal, Object.assign({ rootId: "bottom-banner" }, { children: _jsx(StickyBottomBanner, { brazeMessages: brazeMessages, asyncArticleCount: asyncArticleCount, contentType: CAPI.contentType, sectionName: CAPI.sectionName, section: CAPI.config.section, tags: CAPI.tags, isPaidContent: CAPI.pageType.isPaidContent, isPreview: !!CAPI.isPreview, shouldHideReaderRevenue: CAPI.shouldHideReaderRevenue, isMinuteArticle: CAPI.pageType.isMinuteArticle, isSensitive: CAPI.config.isSensitive, contributionsServiceUrl: CAPI.contributionsServiceUrl, idApiUrl: CAPI.config.idApiUrl, switches: CAPI.config.switches }, void 0) }), void 0)] }, void 0));
};
